import React, { useState, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { HreflangTags } from "../components/SeoHreflang";
import { useSearchParams } from "react-router-dom";
import {
  Box, Typography, Button, Stack, Chip,
} from "@mui/material";
import {
  WhatsApp, Share, ArrowForward, ContentCopy, Check,
  EmojiEvents, WorkspacePremium, MilitaryTech,
} from "@mui/icons-material";

const DOMAIN  = "https://drpawleb.com";
const DISPLAY = '"Cormorant Garamond", Georgia, serif';
const BLUE    = "#1565C0";
const BABY    = "#42A5F5";

interface Entry { n: string; s: number; c?: string; }

const MEDAL_STYLES = [
  { color: "#B45309", bg: "rgba(251,191,36,.12)", border: "rgba(251,191,36,.4)", glow: "rgba(251,191,36,.15)", icon: <EmojiEvents sx={{ fontSize: "1.4rem", color: "#D97706" }} />, label: "Gold" },
  { color: "#475569", bg: "rgba(148,163,184,.1)",  border: "rgba(148,163,184,.35)", glow: "rgba(148,163,184,.12)", icon: <WorkspacePremium sx={{ fontSize: "1.3rem", color: "#64748B" }} />, label: "Silver" },
  { color: "#9A3412", bg: "rgba(251,146,60,.1)",   border: "rgba(251,146,60,.35)", glow: "rgba(251,146,60,.12)", icon: <MilitaryTech sx={{ fontSize: "1.3rem", color: "#EA580C" }} />, label: "Bronze" },
];

const DrPawLogo: React.FC = () => (
  <Stack direction="row" alignItems="center" justifyContent="center" spacing={1.5}>
    <Box sx={{
      width: 42, height: 42, borderRadius: "12px",
      background: "linear-gradient(135deg,#1565C0,#42A5F5)",
      display: "flex", alignItems: "center", justifyContent: "center",
      boxShadow: "0 4px 16px rgba(21,101,192,.35)",
    }}>
      <Typography sx={{ color: "#fff", fontWeight: 900, fontSize: "1.05rem" }}>D</Typography>
    </Box>
    <Typography sx={{ color: "#0A1628", fontWeight: 700, fontSize: "1.65rem", letterSpacing: "-.01em", fontFamily: DISPLAY }}>
      Dr.Paw
    </Typography>
  </Stack>
);

// ─────────────────────────────────────────────────────────────────────────────
const PublicLeaderboard: React.FC = () => {
  const [searchParams] = useSearchParams();
  const quiz   = decodeURIComponent(searchParams.get("quiz")   || "");
  const link   = searchParams.get("link")   || `${DOMAIN}/#pricing`;
  const period = decodeURIComponent(searchParams.get("period") || "This Week");

  const entries: Entry[] = useMemo(() => {
    try {
      const raw = searchParams.get("entries") || "";
      return JSON.parse(decodeURIComponent(atob(raw)));
    } catch { return []; }
  }, [searchParams]);

  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const handleShare = async () => {
    const data = {
      title: `Dr.Paw Leaderboard — ${quiz}`,
      text: `Check out the top vets on the Dr.Paw "${quiz}" leaderboard! 🏆 Can you make the top 10?`,
      url: window.location.href,
    };
    if (typeof navigator.share === "function") { try { await navigator.share(data); return; } catch {} }
    handleCopy();
  };

  const waMsg = encodeURIComponent(
    `🏆 Can you make the Dr.Paw leaderboard?\n\n"${quiz}" — ${entries[0]?.n ?? "the top vet"} is leading with ${entries[0]?.s ?? "100"}%!\n\nChallenge yourself: ${window.location.href}`
  );

  const top3     = entries.slice(0, 3);
  const rest     = entries.slice(3);
  const topScore = entries[0]?.s ?? 100;

  if (!quiz || entries.length === 0) return (
    <Box sx={{ minHeight: "100vh", background: "#F8F3EE", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Typography sx={{ color: "#90A4AE" }}>Invalid or missing leaderboard link.</Typography>
    </Box>
  );

  const metaTitle = quiz ? `${quiz} — Leaderboard | Dr.Paw` : "Top Performers — Dr.Paw Leaderboard";
  const metaDesc  = quiz
    ? `See who topped the "${quiz}" leaderboard this ${period.toLowerCase()}. Can you beat the #1 score? Dr.Paw pet-care courses.`
    : "See this week's top-scoring pet parents on Dr.Paw. Challenge yourself and climb the leaderboard!";

  return (
    <>
    <Helmet>
      <title>{metaTitle}</title>
      <meta name="description" content={metaDesc} />
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDesc} />
      <meta property="og:image" content="https://drpawleb.com/og-image.png" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={metaTitle} />
      <meta name="twitter:description" content={metaDesc} />
    </Helmet>
    <HreflangTags />
    <Box sx={{
      minHeight: "100vh",
      background: "linear-gradient(160deg,#FEF9F5 0%,#F8F3EE 55%,#F2EDE6 100%)",
      display: "flex", flexDirection: "column",
      position: "relative", overflow: "hidden",
    }}>
      <Box sx={{ position: "absolute", top: -100, right: -100, width: 450, height: 450, borderRadius: "50%", background: "radial-gradient(circle,rgba(21,101,192,.07) 0%,transparent 70%)", pointerEvents: "none" }} />
      <Box sx={{ position: "absolute", bottom: -80, left: -80, width: 350, height: 350, borderRadius: "50%", background: "radial-gradient(circle,rgba(66,165,245,.06) 0%,transparent 70%)", pointerEvents: "none" }} />

      {/* Header */}
      <Box sx={{ pt: 4, pb: 2.5, px: 3, textAlign: "center" }}>
        <DrPawLogo />
        <Typography variant="caption" sx={{ display: "block", mt: 0.8, color: BABY, fontWeight: 600, fontSize: ".7rem", letterSpacing: ".15em", textTransform: "uppercase" }}>
          Quiz Leaderboard
        </Typography>
      </Box>

      <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", px: { xs: 2, sm: 3 }, pb: 4 }}>
        <Box sx={{ width: "100%", maxWidth: 580 }}>

          {/* Title + period */}
          <Box sx={{ textAlign: "center", mb: 3.5 }}>
            <Chip label={period} sx={{ bgcolor: "rgba(21,101,192,.1)", color: BLUE, fontWeight: 700, fontSize: ".78rem", border: "1px solid rgba(21,101,192,.25)", mb: 1.5 }} />
            <Typography sx={{ color: "#0A1628", fontWeight: 700, fontSize: { xs: "1.6rem", md: "2.1rem" }, lineHeight: 1.2, fontFamily: DISPLAY, letterSpacing: "-.01em" }}>
              {quiz}
            </Typography>
            <Typography sx={{ color: "#546E7A", fontSize: ".85rem", mt: 0.8 }}>
              Top {entries.length} veterinary professionals
            </Typography>
          </Box>

          {/* Podium — top 3 */}
          {top3.length > 0 && (
            <Stack direction="row" alignItems="flex-end" spacing={1.5} sx={{ mb: 3, px: { xs: 0, sm: 2 } }}>
              {/* 2nd place */}
              {top3[1] && (
                <Box sx={{ flex: 1, textAlign: "center" }}>
                  <Box sx={{
                    background: MEDAL_STYLES[1].bg, border: `1px solid ${MEDAL_STYLES[1].border}`,
                    borderRadius: "18px 18px 0 0", px: 1.5, py: 2,
                    boxShadow: `0 -4px 20px ${MEDAL_STYLES[1].glow}`,
                    minHeight: 130,
                    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end",
                  }}>
                    {MEDAL_STYLES[1].icon}
                    <Typography sx={{ color: MEDAL_STYLES[1].color, fontWeight: 800, fontSize: ".7rem", letterSpacing: ".1em", textTransform: "uppercase", mt: 0.8, mb: 0.4 }}>#2</Typography>
                    <Typography sx={{ color: "#0A1628", fontWeight: 700, fontSize: ".82rem", lineHeight: 1.2, mb: 0.5, fontFamily: DISPLAY, px: 0.5 }}>{top3[1].n}</Typography>
                    {top3[1].c && <Typography sx={{ fontSize: ".95rem", lineHeight: 1 }}>{top3[1].c}</Typography>}
                    <Typography sx={{ color: MEDAL_STYLES[1].color, fontWeight: 800, fontSize: "1.1rem", mt: 0.5, fontFamily: DISPLAY }}>{top3[1].s}%</Typography>
                  </Box>
                </Box>
              )}

              {/* 1st place */}
              <Box sx={{ flex: 1.2, textAlign: "center" }}>
                <Box sx={{
                  background: MEDAL_STYLES[0].bg, border: `1px solid ${MEDAL_STYLES[0].border}`,
                  borderRadius: "18px 18px 0 0", px: 1.5, py: 2.5,
                  boxShadow: `0 -8px 28px ${MEDAL_STYLES[0].glow}`,
                  minHeight: 170,
                  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end",
                }}>
                  <EmojiEvents sx={{ fontSize: "2rem", color: "#D97706" }} />
                  <Typography sx={{ color: "#B45309", fontWeight: 800, fontSize: ".7rem", letterSpacing: ".1em", textTransform: "uppercase", mt: 1, mb: 0.5 }}>#1 Champion</Typography>
                  <Typography sx={{ color: "#0A1628", fontWeight: 700, fontSize: ".9rem", lineHeight: 1.2, mb: 0.5, fontFamily: DISPLAY, px: 0.5 }}>{top3[0].n}</Typography>
                  {top3[0].c && <Typography sx={{ fontSize: "1.1rem", lineHeight: 1 }}>{top3[0].c}</Typography>}
                  <Typography sx={{ color: "#B45309", fontWeight: 800, fontSize: "1.4rem", mt: 0.5, fontFamily: DISPLAY }}>{top3[0].s}%</Typography>
                </Box>
              </Box>

              {/* 3rd place */}
              {top3[2] && (
                <Box sx={{ flex: 1, textAlign: "center" }}>
                  <Box sx={{
                    background: MEDAL_STYLES[2].bg, border: `1px solid ${MEDAL_STYLES[2].border}`,
                    borderRadius: "18px 18px 0 0", px: 1.5, py: 2,
                    boxShadow: `0 -4px 20px ${MEDAL_STYLES[2].glow}`,
                    minHeight: 110,
                    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end",
                  }}>
                    {MEDAL_STYLES[2].icon}
                    <Typography sx={{ color: MEDAL_STYLES[2].color, fontWeight: 800, fontSize: ".7rem", letterSpacing: ".1em", textTransform: "uppercase", mt: 0.8, mb: 0.4 }}>#3</Typography>
                    <Typography sx={{ color: "#0A1628", fontWeight: 700, fontSize: ".82rem", lineHeight: 1.2, mb: 0.5, fontFamily: DISPLAY, px: 0.5 }}>{top3[2].n}</Typography>
                    {top3[2].c && <Typography sx={{ fontSize: ".95rem", lineHeight: 1 }}>{top3[2].c}</Typography>}
                    <Typography sx={{ color: MEDAL_STYLES[2].color, fontWeight: 800, fontSize: "1.1rem", mt: 0.5, fontFamily: DISPLAY }}>{top3[2].s}%</Typography>
                  </Box>
                </Box>
              )}
            </Stack>
          )}

          {/* Podium base bar */}
          <Box sx={{ height: 8, background: "linear-gradient(90deg,rgba(21,101,192,.04),rgba(21,101,192,.12),rgba(21,101,192,.04))", borderRadius: "0 0 4px 4px", mb: 3 }} />

          {/* Ranks 4–10 */}
          {rest.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Stack spacing={1}>
                {rest.map((e, i) => {
                  const rank   = i + 4;
                  const barPct = topScore > 0 ? (e.s / topScore) * 100 : 0;
                  return (
                    <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 2, p: { xs: 1.5, md: 2 }, borderRadius: "13px", background: "#fff", border: "1px solid rgba(21,101,192,.1)" }}>
                      <Box sx={{ width: 32, height: 32, borderRadius: "9px", bgcolor: "rgba(21,101,192,.07)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <Typography sx={{ color: BLUE, fontWeight: 800, fontSize: ".78rem" }}>#{rank}</Typography>
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.6 }}>
                          <Typography sx={{ color: "#37474F", fontWeight: 600, fontSize: ".88rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {e.c && <span style={{ marginRight: 6 }}>{e.c}</span>}{e.n}
                          </Typography>
                          <Typography sx={{ color: BLUE, fontWeight: 700, fontSize: ".88rem", ml: 1, flexShrink: 0, fontFamily: DISPLAY }}>{e.s}%</Typography>
                        </Stack>
                        <Box sx={{ height: 4, borderRadius: 99, bgcolor: "rgba(21,101,192,.1)", overflow: "hidden" }}>
                          <Box sx={{ height: "100%", width: `${barPct}%`, background: "linear-gradient(90deg,#1565C0,#42A5F5)", borderRadius: 99 }} />
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          )}

          {/* Challenge CTA */}
          <Box sx={{ background: "rgba(251,191,36,.07)", border: "1px solid rgba(251,191,36,.25)", borderRadius: "20px", p: { xs: 2.5, md: 3 }, mb: 3, textAlign: "center" }}>
            <Typography sx={{ color: "#B45309", fontWeight: 700, fontSize: "1rem", mb: 0.8, fontFamily: DISPLAY }}>
              🏆 Can you beat {entries[0]?.n?.split(" ")[0] ?? "the leader"}'s {entries[0]?.s}%?
            </Typography>
            <Typography sx={{ color: "#546E7A", fontSize: ".85rem", mb: 2 }}>
              Take the quiz now and compete for the top spot.
            </Typography>
            <Button
              variant="contained" href={link} endIcon={<ArrowForward />} fullWidth
              sx={{ py: 1.5, borderRadius: "50px", textTransform: "none", fontWeight: 800, background: "linear-gradient(135deg,#1565C0,#42A5F5)", boxShadow: "0 6px 20px rgba(21,101,192,.3)", "&:hover": { transform: "translateY(-2px)" }, transition: "all .2s ease" }}
            >
              Take the Quiz Now
            </Button>
          </Box>

          {/* Share */}
          <Stack spacing={1.5} sx={{ mb: 2 }}>
            <Button
              variant="contained" startIcon={<Share />} onClick={handleShare} fullWidth
              sx={{ py: 1.4, borderRadius: "50px", textTransform: "none", fontWeight: 700, background: "linear-gradient(135deg,#7C3AED,#A855F7)", boxShadow: "0 4px 14px rgba(124,58,237,.25)", "&:hover": { background: "linear-gradient(135deg,#6D28D9,#7C3AED)", transform: "translateY(-1px)" }, transition: "all .2s ease" }}
            >
              Share the Leaderboard
            </Button>
            <Button
              variant="outlined" startIcon={<WhatsApp />} href={`https://wa.me/?text=${waMsg}`} target="_blank" fullWidth
              sx={{ py: 1.3, borderRadius: "50px", textTransform: "none", fontWeight: 700, borderColor: "#25D366", color: "#25D366", "&:hover": { bgcolor: "rgba(37,211,102,.08)" } }}
            >
              Challenge Colleagues on WhatsApp
            </Button>
          </Stack>

          <Button
            variant="outlined" startIcon={copied ? <Check /> : <ContentCopy />} onClick={handleCopy} fullWidth
            sx={{ py: 1.2, borderRadius: "50px", textTransform: "none", fontWeight: 600, borderColor: copied ? "rgba(34,197,94,.4)" : "rgba(21,101,192,.25)", color: copied ? "#22C55E" : "#78909C", bgcolor: "#fff", transition: "all .2s ease" }}
          >
            {copied ? "Link Copied!" : "Copy Leaderboard Link"}
          </Button>
        </Box>
      </Box>

      <Box sx={{ py: 3, textAlign: "center", borderTop: "1px solid rgba(21,101,192,.1)" }}>
        <Typography variant="caption" sx={{ color: "#B0BEC5", fontSize: ".72rem" }}>
          © Dr.Paw — Empowering Veterinary Professionals Worldwide
        </Typography>
      </Box>
    </Box>
    </>
  );
};

export default PublicLeaderboard;
